// Agenda.tsx
import React, { useContext, useEffect, useState } from 'react';
import { Container, Button, Row, Col, Input, FormGroup, Label } from 'reactstrap';

import { Calendar, momentLocalizer, SlotInfo } from 'react-big-calendar';
import withDragAndDrop, { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import 'moment/locale/pt-br';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Select from 'react-select';

import type { View } from 'react-big-calendar';

import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import Header from '../../components/Headers/Header';
import AdminNavbar from '../../components/Navbars/AdminNavbar/AdminNavbar';
import { AuthContext } from '../../context/AuthContext';
import { AgendaContext } from '../../context/AgendaContext';
import { AgendaType, CreateAgendaType, EditAgendaType } from '../../context/hooks/useAgenda';
import { TipoAgendaContext } from '../../context/TipoAgendaContext';
import { TerceiroContext } from '../../context/TerceiroContext';
import { TerceiroType } from '../../context/hooks/useTerceiro';
import useUser from '../../context/hooks/useUser';

import { CreateAgendaModal } from '../../components/Agenda/CreateAgendaModal';
import { EditAgendaModal } from '../../components/Agenda/EditAgendaModal';

moment.locale('pt-br');
const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

const messages = {
    allDay: 'Dia inteiro',
    previous: 'Anterior',
    next: 'Próximo',
    today: 'Hoje',
    month: 'Mês',
    week: 'Semana',
    day: 'Dia',
    agenda: 'Agenda',
    date: 'Data',
    time: 'Hora',
    event: 'Evento',
    noEventsInRange: 'Nenhum evento nesse período.',
    showMore: (total: number) => `+ Ver mais (${total})`,
};

const Agenda: React.FC = () => {
    const { objUserTk } = useContext(AuthContext);
    const {
        getAgendaPaged,
        updateAgenda,
        listAgenda,
        downloadAgendaPdfByDate,
        downloadAgendaPdfCustom,
        printAgendaPdfByDate,
        printAgendaPdfCustom,
        listAllAgenda,
    } = useContext(AgendaContext);

    const { getAllTipoAgendaPaged } = useContext(TipoAgendaContext);
    const { getPermissionsByUser } = useUser();
    const { getTerceiroPaged, handleGetTerceiroByAgendaId } = useContext(TerceiroContext);

    const [loaging, setLoading] = useState(true);
    const [newAgenda, setNewAgenda] = useState<CreateAgendaType>({
        description: "",
        location: "",
        responsible: "",
        startDate: "",
        endDate: "",
        beca: "",
        tipoAgendaId: 0,
        empresaId: objUserTk?.EmpresaId ?? 0,
        contratoId: null,
        terceiros: [],
    });

    const [editingAgenda, setEditingAgenda] = useState<EditAgendaType | null>(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedTerceiros, setSelectedTerceiros] = useState<TerceiroType[]>([]);
    const [selectedDate, setSelectedDate] = useState<string>("");

    const [customStartDate, setCustomStartDate] = useState<string>("");
    const [customEndDate, setCustomEndDate] = useState<string>("");
    const [selectedAgendaIds, setSelectedAgendaIds] = useState<number[]>([]);

    const [currentView, setCurrentView] = useState<View>('month');
    const today = new Date();
    const agendaBaseDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 365);

    useEffect(() => {
        if (!objUserTk?.EmpresaId) return;
        setLoading(true);

        // Paraleliza as chamadas de API independentes para melhorar performance
        Promise.all([
            getAllTipoAgendaPaged(1, 1000, objUserTk.EmpresaId),
            getTerceiroPaged(1, "", objUserTk.EmpresaId, 1000, "Fotógrafo"),
        ]).then(() => {
            return listAllAgenda(objUserTk.EmpresaId);
        })
            .then(() => setLoading(false))
            .catch((error) => {
                console.error("Erro ao buscar agenda:", error);
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        const fetchPermissions = async () => {
            if (objUserTk?.UserId) {
                await getPermissionsByUser(objUserTk.UserId);
            }
        };

        fetchPermissions().catch((error) => console.error("Erro ao carregar permissões:", error));
    }, [objUserTk?.UserId]);

    useEffect(() => {
        if (objUserTk?.EmpresaId) {
            setNewAgenda((prev) => ({ ...prev, empresaId: objUserTk.EmpresaId }));
        }
    }, [objUserTk?.EmpresaId]);

    const handleEditAgenda = (agenda: AgendaType) => {
        const updateData: EditAgendaType = {
            id: agenda.id,
            description: agenda.description,
            location: agenda.location,
            responsible: agenda.responsible,
            startDate: agenda.startDate,
            endDate: agenda.endDate,
            beca: agenda.beca,
            cursoId: agenda.cursoId ?? null,
            cursoName: agenda.cursoName ?? null,
            cursoIds: agenda.cursoIds ?? [],
            cursoNames: agenda.cursoNames ?? [],
            step: agenda.step,
            tipoAgendaId: agenda.tipoAgendaId,
        };
        setEditingAgenda(updateData);
        handleGetTerceiroByAgendaId(agenda.id);
        setEditModalOpen(true);
    };

    const handleSelectSlot = (slotInfo: SlotInfo) => {
        setNewAgenda({
            ...newAgenda,
            startDate: moment(slotInfo.start).format('YYYY-MM-DDTHH:mm'),
            endDate: moment(slotInfo.end).format('YYYY-MM-DDTHH:mm'),
        });
        setSelectedTerceiros([]);
        setCreateModalOpen(true);
    };

    const handleSelectEvent = (event: any, e: React.SyntheticEvent<HTMLElement>) => {
        e.preventDefault();
        const agenda = listAgenda.find((a) => a.id === event.id);
        if (agenda) {
            handleEditAgenda(agenda);
        }
    };

    const handleEventDrop = (args: EventInteractionArgs<any>) => {
        const { event, start, end } = args;
        const updatedAgenda = listAgenda.find((a) => a.id === event.id);
        if (updatedAgenda) {
            updateAgenda({
                ...updatedAgenda,
                startDate: moment(start).format('YYYY-MM-DDTHH:mm'),
                endDate: moment(end).format('YYYY-MM-DDTHH:mm'),
            }).then(() => getAgendaPaged(1, objUserTk.EmpresaId, 9));
        }
    };

    const events = listAgenda.map((agenda: AgendaType) => ({
        id: agenda.id,
        title: agenda.description,
        start: new Date(agenda.startDate),
        end: new Date(agenda.endDate),
        cursoName: agenda.cursoName ?? "-",
    }));

    const handleDownloadPdf = () => {
        if (selectedDate) {
            const date = new Date(selectedDate);
            downloadAgendaPdfByDate(date);
            setSelectedDate("");
        }
    };

    const handlePrintPdf = () => {
        if (selectedDate) {
            const date = new Date(selectedDate);
            printAgendaPdfByDate(date);
            setSelectedDate("");
        }
    };

    const handleDownloadCustomPdf = () => {
        const filtrosVazios =
            !customStartDate &&
            !customEndDate &&
            (!selectedAgendaIds || selectedAgendaIds.length === 0);

        if (filtrosVazios) {
            downloadAgendaPdfCustom({
                empresaId: objUserTk.EmpresaId,
                startDate: undefined,
                endDate: undefined,
                agendaIds: undefined,
            });
        } else {
            downloadAgendaPdfCustom({
                empresaId: objUserTk.EmpresaId,
                startDate: customStartDate || undefined,
                endDate: customEndDate || undefined,
                agendaIds: selectedAgendaIds.length > 0 ? selectedAgendaIds : undefined,
            });
        }
    };

    const handlePrintCustomPdf = () => {
        const filtrosVazios =
            !customStartDate &&
            !customEndDate &&
            (!selectedAgendaIds || selectedAgendaIds.length === 0);

        if (filtrosVazios) {
            printAgendaPdfCustom({
                empresaId: objUserTk.EmpresaId,
                startDate: undefined,
                endDate: undefined,
                agendaIds: undefined,
            });
        } else {
            printAgendaPdfCustom({
                empresaId: objUserTk.EmpresaId,
                startDate: customStartDate || undefined,
                endDate: customEndDate || undefined,
                agendaIds: selectedAgendaIds.length > 0 ? selectedAgendaIds : undefined,
            });
        }
    };

    const options = listAgenda.map((agenda) => ({
        value: agenda.id,
        label: `${agenda.id} - ${agenda.description}`,
    }));

    const handleMultiSelectChange = (selectedOptions: any) => {
        const selectedIds = selectedOptions ? selectedOptions.map((opt: any) => opt.value) : [];
        setSelectedAgendaIds(selectedIds);
    };

    const CustomAgendaEvent = ({ event }: { event: any }) => (
        <div>
            <div><strong>{event.title}</strong></div>
            <div><small><strong>Curso:</strong> {event.cursoName}</small></div>
        </div>
    );

    return (
        <>
            <LoadingSpinner />
            <Header />
            <AdminNavbar brandText={"Gerenciar Agenda"} search={false} titleSearch={""} />

            <Container className="mt-5">
                <Row className="align-items-center mb-4">
                    <Col xs="12" md="2" lg="2">
                        <Label className="form-control-label">Data inicial</Label>
                        <Input
                            className="form-control-alternative"
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                        />
                    </Col>
                    <Col xs="12" md="2" lg="2">
                        <Label className="form-control-label">Data final</Label>
                        <Input
                            className="form-control-alternative"
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                        />
                    </Col>
                    <Col xs="12" md="4" lg="4" className="d-flex justify-content-start justify-content-md-start mb--4">
                        <FormGroup>
                            <Label className="form-control-label">Selecionar eventos específicos</Label>
                            <Select
                                className="form-control-alternative"
                                isMulti
                                name="agendas"
                                options={options}
                                classNamePrefix="select"
                                onChange={handleMultiSelectChange}
                                value={options.filter((opt) => selectedAgendaIds.includes(opt.value))}
                                placeholder="Digite para buscar eventos..."
                                styles={{
                                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                                menuPortalTarget={document.body}
                            />
                        </FormGroup>
                    </Col>
                    <Col xs="12" md="4" lg="4" className="d-flex justify-content-start justify-content-md-end mb-2">
                        <Button style={{ color: "#FFF", backgroundColor: "#585858" }} size="m" onClick={handlePrintCustomPdf}>
                            Imprimir PDF por Período/Eventos
                        </Button>
                        <Button className="ml-2" style={{ color: "#FFF", backgroundColor: "#585858" }} size="m" onClick={handleDownloadCustomPdf}>
                            Baixar PDF por Período/Eventos
                        </Button>
                    </Col>
                </Row>

                <Row className="mt-5">
                    <Col>
                        <DnDCalendar
                            localizer={localizer}
                            events={events}
                            startAccessor={(event: any) => new Date(event.start)}
                            endAccessor={(event: any) => new Date(event.end)}
                            selectable
                            onSelectSlot={handleSelectSlot}
                            onSelectEvent={handleSelectEvent}
                            onEventDrop={handleEventDrop}
                            resizable
                            messages={messages}
                            view={currentView}
                            onView={(view) => setCurrentView(view)}
                            views={{ month: true, week: true, day: true, agenda: true }}
                            date={currentView === 'agenda' ? agendaBaseDate : undefined}
                            length={730}
                            formats={{
                                agendaDateFormat: (date) => moment(date).format('dddd, DD [de] MMMM [de] YYYY'),
                                agendaTimeFormat: 'HH:mm',
                                timeGutterFormat: 'HH:mm',
                            }}
                            style={{ height: 500 }}
                            components={{
                                agenda: {
                                    event: CustomAgendaEvent,
                                },
                            }}
                        />
                    </Col>
                </Row>
            </Container>

            <CreateAgendaModal
                isOpen={createModalOpen}
                toggle={() => setCreateModalOpen(false)}
                defaultStart={newAgenda.startDate}
                defaultEnd={newAgenda.endDate}
                onCreated={() => listAllAgenda(objUserTk.EmpresaId)}
            />

            <EditAgendaModal
                isOpen={editModalOpen}
                toggle={() => setEditModalOpen(false)}
                agenda={editingAgenda}
                onUpdated={() => listAllAgenda(objUserTk.EmpresaId)}
                onDeleted={() => listAllAgenda(objUserTk.EmpresaId)}
            />
        </>
    );
};

export default Agenda;
